import { formatDate } from '@angular/common';
import { Injectable } from '@angular/core';
import { ActivityReportExtended, VacationRequestExtended } from '@shared/factories';
import { InfoStateModel                                  } from '@shared/models';

type StateLook = Pick<InfoStateModel, 'icon' | 'color'>;

const APPROVED: StateLook = { icon: 'icon-tick',        color: 'color-green'  };
const REJECTED: StateLook = { icon: 'icon-cross',       color: 'color-red'    };
const ARCHIVED: StateLook = { icon: 'icon-warning',     color: 'color-orange' };
const PENDING:  StateLook = { icon: 'icon-circle-time', color: 'color-orange' };
const CHECKED:  StateLook = { icon: 'icon-tick'                               };

const BY_TEMPTON                   = ' von Tempton';
const ON_EMPLOYEE_DEVICE           = ' am Gerät des Mitarbeiters';
const EMPLOYEE_SUFFIX              = ' (Mitarbeiter)';

const INITIALLY_REJECTED_ON_DEVICE = 'Kundenfreigabe zunächst' + ON_EMPLOYEE_DEVICE + ' abgelehnt, später im Portal';

const AR_AUTO_ARCHIVE_TOOLTIP      = 'Dieser Datensatz wurde automatisch archiviert, da dieser 3 Monate nicht bearbeitet wurde.';
const VR_AUTO_ARCHIVE_TOOLTIP      = 'Dieser Datensatz wurde automatisch archiviert, da das Startdatum des Urlaubs bereits mehr als 3 Monate in der Vergangenheit liegt.';

@Injectable({
  providedIn: 'root'
})
export class InfoStatesCollectorService {

  collectActivityReportStates(ar: ActivityReportExtended, isInternal: boolean = false): InfoStateModel[] {
    return isInternal
      ? this.collectActivityReportStatesForInternal(ar)
      : this.collectActivityReportStatesForCustomer(ar);
  }

  collectVacationRequestStates(vr: VacationRequestExtended, isInternal: boolean = false): InfoStateModel[] {
    return isInternal
      ? this.collectVacationRequestStatesForInternal(vr)
      : this.collectVacationRequestStatesForCustomer(vr);
  }

  private collectActivityReportStatesForCustomer(ar: ActivityReportExtended): InfoStateModel[] {
    const states: InfoStateModel[] = [];

    if (!ar.attachment_url && (ar.approvedByCustomer || ar.rejectedByCustomer)) states.push(this.mapActivityReportCustomerReview(ar, false));
    if (ar.approvedByInternal)                                                  states.push(this.mapActivityReportInternalReview(ar, false));
    if (ar.archivedState)                                                       states.push(this.mapActivityReportArchiveState(  ar, false));

    return states;
  }

  private collectActivityReportStatesForInternal(ar: ActivityReportExtended): InfoStateModel[] {
    const states: InfoStateModel[] = [];

    if (ar.awaitingForCustomer && !ar.archived_at) states.push(this.mapActivityReportAwaitingCustomer(ar));
    if (!ar.attachment_url && ar.customer_review)  states.push(this.mapActivityReportCustomerReview(ar, true));
    if (ar.checked_time_at)                        states.push({ ...CHECKED, info: 'Intern geprüft', by: ar.checked_time_by?.name, at: ar.checked_time_at });
    if (ar.approvedByInternal)                     states.push(this.mapActivityReportInternalReview(ar, true));
    if (ar.archivedState)                          states.push(this.mapActivityReportArchiveState(  ar, true));

    return states;
  }

  private mapActivityReportAwaitingCustomer(ar: ActivityReportExtended): InfoStateModel {
    return {
      ...PENDING,
      info:       'Kundenfreigabe erfolgt später',
      extraClass: 'color-orange font-s',
      extra:      ar.customerReviewWaitingPeriodMessage ? `(${ar.customerReviewWaitingPeriodMessage})` : null
    };
  }

  private mapActivityReportCustomerReview(ar: ActivityReportExtended, isInternal: boolean): InfoStateModel {
    const state: InfoStateModel = { ...(ar.approvedByCustomer ? APPROVED : REJECTED), by: ar.reviewed_by_customer?.name };

    if (ar.initiallyRejectedMobile && ar.approvedByCustomerPortals) return { ...state, info: INITIALLY_REJECTED_ON_DEVICE + ' freigegeben',       at: ar.customer_reviewed_at };
    if (ar.initiallyRejectedMobile && ar.rejectedByCustomerPortals) return { ...state, info: INITIALLY_REJECTED_ON_DEVICE + ' erneute Ablehnung', at: ar.customer_reviewed_at };

    if (isInternal && ar.rejectedByCustomerMobile) return { ...state, info: 'Klärungsbedarf / Kunde gibt nicht frei' };

    const onDevice = ar.approvedByCustomerMobile || ar.rejectedByCustomerMobile;
    const action   = ar.approvedByCustomer ? ' erteilt' : ar.rejectedByCustomer ? ' abgelehnt' : '';

    return {
      ...state,
      info: 'Kundenfreigabe' + (onDevice ? ON_EMPLOYEE_DEVICE : '') + action,
      at:   onDevice ? ar.created_at : ar.customer_reviewed_at
    };
  }

  private mapActivityReportInternalReview(ar: ActivityReportExtended, isInternal: boolean): InfoStateModel {
    let info = ar.rejectedByCustomer ? 'Nach Klärung freigegeben' : 'Freigegeben';

    if (!ar.attachment_url && !ar.customer_review) info += ' ohne Kundenbestätigung';
    if (!isInternal)                               info += BY_TEMPTON;

    return { ...APPROVED, info, by: ar.archived_by?.name, at: ar.archived_at };
  }

  private mapActivityReportArchiveState(ar: ActivityReportExtended, isInternal: boolean): InfoStateModel {
    const archived  = ar.archivedBySystem || ar.archivedByInternal || ar.archivedByExternal;
    const byTempton = ar.archivedBySystem || ar.archivedByInternal || ar.deletedByInternal;

    let info = ar.archivedBySystem   ? 'Automatisch archiviert' :
               ar.archivedByInternal ? 'Manuell archiviert'     :
               ar.archivedByExternal ? 'Manuell archiviert'     :
               ar.deletedByInternal  ? 'Abgelehnt'              :
               ar.deletedByCustomer  ? 'Vom Kunden abgelehnt'   : '';

    if (!isInternal && byTempton) info += BY_TEMPTON;
    if (ar.archived_at          ) info += ` am ${formatDate(ar.archived_at, ar.archivedBySystem ? 'dd.MM.yyyy' : 'dd.MM.yyyy HH:mm', 'de')}`;

    const state: InfoStateModel = { ...(archived ? ARCHIVED : REJECTED), info };

    if (ar.archived_by?.name) state.by      = ar.archived_by.name + (ar.archivedByExternal ? EMPLOYEE_SUFFIX : '');
    if (ar.archivedBySystem)  state.tooltip = AR_AUTO_ARCHIVE_TOOLTIP;

    return state;
  }

  private collectVacationRequestStatesForCustomer(vr: VacationRequestExtended): InfoStateModel[] {
    const states: InfoStateModel[] = [];

    if (vr.assignment && !vr.createdByInternal && vr.customerReview) states.push(this.mapVacationRequestCustomerReview(vr, false));
    if (vr.internalReview)                                           states.push(this.mapVacationRequestInternalReview(vr, false));

    return [...states, ...this.collectVacationRequestArchiveStates(vr)];
  }

  private collectVacationRequestStatesForInternal(vr: VacationRequestExtended): InfoStateModel[] {
    const states:  InfoStateModel[] = [];
    const reviews: InfoStateModel[] = [];

    if (vr.createdByInternal)                                              states.push({ ...CHECKED, info: 'Erstellt', by: vr.createdBy?.name, at: vr.createdAt });
    if (vr.awaitingExternalReview && !vr.externalReview && !vr.archivedAt) states.push({ ...PENDING, info: 'Erwarte Mitarbeiter Freigabe' });
    if (vr.createdByInternal      &&  vr.externalReview)                   states.push(this.mapVacationRequestExternalReview(vr));

    if (vr.assignment && !vr.createdByInternal) {
      if (!vr.customerReview && !vr.internalReview && !vr.archivedAt) states.push({ ...PENDING, info: 'Kundenfreigabe erfolgt später' });
      if ( vr.customerReview)                                         reviews.push(this.mapVacationRequestCustomerReview(vr, true));
    }

    if (vr.precheckedByInternalAt) reviews.push({ ...CHECKED, info: 'Intern geprüft', by: vr.precheckedByInternal?.name, at: vr.precheckedByInternalAt });
    if (vr.internalReview)         reviews.push(this.mapVacationRequestInternalReview(vr, true));

    reviews.sort((a, b) => a.at.getTime() - b.at.getTime());

    return [...states, ...reviews, ...this.collectVacationRequestArchiveStates(vr)];
  }

  private collectVacationRequestArchiveStates(vr: VacationRequestExtended): InfoStateModel[] {
    if (vr.archivedBySystem)   return [{ ...ARCHIVED, info: 'Automatisch archiviert', at: vr.archivedAt,         tooltip: VR_AUTO_ARCHIVE_TOOLTIP }];
    if (vr.archivedByExternal) return [{ ...ARCHIVED, info: 'Manuell archiviert',     at: vr.externalReviewedAt, by: vr.externalEmployee.name + EMPLOYEE_SUFFIX }];
    return [];
  }

  private mapVacationRequestExternalReview(vr: VacationRequestExtended): InfoStateModel {
    const approved = vr.externalReview === 'approved';

    return {
      ...(approved ? APPROVED : REJECTED),
      info: approved ? 'Akzeptiert' : 'Abgelehnt',
      by:   vr.reviewedByExternal?.name,
      at:   vr.externalReviewedAt
    };
  }

  private mapVacationRequestCustomerReview(vr: VacationRequestExtended, isInternal: boolean): InfoStateModel {
    const approved = vr.customerReview === 'approved';
    const rejected = vr.customerReview === 'rejected';

    const signedOnDevice = approved && !!vr.customerSignatureUrl;
    const onDevice       = signedOnDevice || (rejected && !vr.customerRejectionReason);
    const device         = onDevice ? ON_EMPLOYEE_DEVICE : '';

    let info: string;
    if      (isInternal)                 info = 'Kundenfreigabe'    + device + (approved ? ' erteilt' : rejected ? ' abgelehnt' : '');
    else if (approved)                   info = 'Kundenbestätigung' + device +             ' erteilt';
    else if (vr.customerRejectionReason) info = 'Nicht bestätigt';
    else                                 info = 'Keine Bestätigung' + device + (rejected ? ' erteilt' : '');

    return {
      ...(approved ? APPROVED : REJECTED),
      info,
      by: vr.reviewedByCustomer?.name,
      at: signedOnDevice ? vr.createdAt : vr.customerReviewedAt
    };
  }

  private mapVacationRequestInternalReview(vr: VacationRequestExtended, isInternal: boolean): InfoStateModel {
    const approved = vr.internalReview === 'approved';
    const archived = vr.archivedByInternal || (vr.internalReview === 'rejected' && vr.internalRejectionReason === 'obsolete');

    let context = '';
    if (vr.assignment) {
      const reviewerRejected = vr.createdByInternal ? vr.externalReview === 'rejected' : vr.customerReview === 'rejected';

      if (approved && reviewerRejected)                             context = 'Nach Klärung';
      if (!vr.createdByInternal && !vr.customerReview && !archived) context = 'Ohne Bestätigung des Kunden';
    }

    const [standalone, afterContext] =
      approved              ? ['Freigegeben',        'freigegeben'] :
      archived              ? ['Manuell archiviert', 'archiviert' ] :
      vr.rejectedByExternal ? ['Archiviert',         'archiviert' ] :
                              ['Abgelehnt',          'abgelehnt'  ];

    let info = context ? `${context} ${afterContext}` : standalone;
    if (!isInternal) info += BY_TEMPTON;

    return {
      ...(approved ? APPROVED : archived ? ARCHIVED : REJECTED),
      info,
      by: vr.reviewedByInternal?.name,
      at: vr.internalReviewedAt
    };
  }

  mapMilegeMoneyVersionHistoryItem(report): string {
    if (!report.event) return '';

    const event: string = report.event;
    const time = `${formatDate(report.createdAt, 'HH:mm', 'de')} Uhr`;
    const date =    formatDate(report.createdAt, 'dd.MM.yyyy', 'de');
    const by   = report.updatedBy?.name ? `von ${report.updatedBy.name}` : '';

    if (event.includes('edit')) {
      return report.updatedByUserGroup === 'customer'
        ? `Version durch Kundenänderungen am TN erzeugt um ${time} am ${date} ${by}`
        : `Geändert: ${time} ${date} ${by}`;
    }
    if (event.includes('approve'))                              return `Genehmigt: ${time} ${date} ${by}`;
    if (event.includes('reject'))                               return `Abgelehnt: ${time} ${date} ${by}`;
    if (event.includes('create') || event.includes('recovery')) return `Erstellt: ${time} ${date}`;
  }

}
